// The market lab (`?scene=market`, phase D1: gates D1.1 and D1.2) as a variant of the valley's machinery: the Wanderer
// on its controller, the camera and the look, on a flat walled square, with the joined fight (combat, the crowd, the
// bodies; no encounter: specs and the handle spawn the enemies to defeat), the things (herbs, flowers and ore to
// gather, a crate and a pot to break; their pickups and the chooser) and the rpg layer (module.ts): the merchant and the
// smith (NPC stand-ins until the town stream's npcs.json), a forge and a cooking pot, as interactables in the things'
// chooser. The things put what they gather and collect into the rpg layer's inventory.
import type { BodySpec } from "@rpg/collision";
import type { Runtime } from "@rpg/runtime";
import { buildNavWorld, navGeometry, type NavWorld } from "@rpg/nav";
import type { SpikeContent } from "../content";
import { FIGHT_LAYERS, FIGHT_SOLVER_ITERATIONS, createFight, readFightContent, type Fight } from "../enemies/fight";
import { VALLEY_LAYERS, type Course } from "../valley/course";
import { navShapesOf } from "../valley/fight";
import type { ValleyVariant } from "../valley/scene";
import { readThingsContent } from "../things/content";
import { BREAKABLE_IDS, THINGS_LAYERS, createThings, dynamicNavShapes, type Things } from "../things/module";
import { Conditions, standingFacts, tieredStanding, type StandingProvider } from "@rpg/story";
import { readStoryContent } from "../story/content";
import { RPG_FAULTS, readRpgContent, type RpgContent } from "./content";
import { createRpg, type Rpg } from "./module";

/** The square's half size, its walls, where the hero starts (facing −Z: the stalls) and the stub mount. */
export const MARKET_HALF = 18;
export const MARKET_SPAWN = { x: 0, z: 6, yaw: Math.PI };
const MARKET_MOUNT = { x: -15, z: 15, yaw: Math.PI };
const WALL = 17.5;
/** Where specs spawn the enemies to defeat: the open north of the square. */
export const MARKET_ARENA = { x: 0, z: -11 };
/** The navmesh: the valley's agent on 12 m tiles. */
const MARKET_NAV = { cellSize: 0.25, cellHeight: 0.1, tileSize: 48, agentRadius: 0.4, agentHeight: 1.6, agentClimb: 0.35, maxSlope: 40, bounds: [[-MARKET_HALF, -2, -MARKET_HALF], [MARKET_HALF, 6, MARKET_HALF]] as [[number, number, number], [number, number, number]] };

/** The square is flat. */
export const marketHeight = (_x: number, _z: number): number => 0;

/** The square's static course: flat ground and four walls. */
export function marketCourse(): Course {
  const bodies: BodySpec[] = [{ id: "terrain", kind: "fixed", position: { x: 0, y: -0.5, z: 0 }, colliders: [{ shape: { type: "cuboid", halfExtents: { x: MARKET_HALF, y: 0.5, z: MARKET_HALF } }, layer: "terrain", tag: "grass" }] }];
  for (const [id, x, z, hx, hz] of [
    ["wall-n", 0, -WALL, WALL, 0.4],
    ["wall-s", 0, WALL, WALL, 0.4],
    ["wall-w", -WALL, 0, 0.4, WALL],
    ["wall-e", WALL, 0, 0.4, WALL],
  ] as const)
    bodies.push({ id, kind: "fixed", position: { x, y: 1.2, z }, colliders: [{ shape: { type: "cuboid", halfExtents: { x: hx, y: 1.2, z: hz } }, layer: "solid", tag: "stone" }] });
  return { bodies, water: [], steps: [], ramps: [], stepLaneZ: 0 };
}

/** The lab's parts for the sim mount. */
export interface MarketLabMount {
  variant: ValleyVariant;
  content: RpgContent;
  /** @rpg/story's conditions (no story in the lab: the town's standing alone, from the story table's factions). */
  conditions: Conditions;
  standing: StandingProvider;
  rpg(): Rpg;
  things(): Things;
  fight(): Fight;
}

/** The market lab. Throws when the rpg content doesn't hold together (its issues listed). */
export function marketLab(o: { content: SpikeContent; runtime: Runtime }): MarketLabMount {
  const fightContent = readFightContent(o.content);
  const content = readRpgContent(o.content, RPG_FAULTS);
  if (content.issues.length) throw new Error(`market: the rpg content has problems: ${content.issues.join("; ")}`);
  const thingsContent = readThingsContent(o.content, content.marketThings);
  const standing = tieredStanding(readStoryContent(o.content.registry).factions);
  const conditions = new Conditions().register(standingFacts(standing));
  let fight: Fight | null = null;
  let things: Things | null = null;
  let rpg: Rpg | null = null;
  const variant: ValleyVariant = {
    name: "market",
    layers: { ...VALLEY_LAYERS, ...FIGHT_LAYERS, ...THINGS_LAYERS },
    course: () => marketCourse(),
    height: marketHeight,
    spawn: MARKET_SPAWN,
    mountSpawn: MARKET_MOUNT,
    solverIterations: FIGHT_SOLVER_ITERATIONS,
    extend({ rig, rng, actions }) {
      const nav: NavWorld = buildNavWorld(navGeometry(navShapesOf(rig.course.bodies)), MARKET_NAV, { dynamic: dynamicNavShapes(thingsContent, marketHeight) });
      const r = createRpg({ content, rng, fight: () => f, things: () => t, conditions, groundAt: marketHeight });
      const f = createFight({
        collision: rig.collision,
        actions,
        rng,
        nav,
        content: fightContent,
        slowMo: (scale, s, ease) => o.runtime.clock.slowMo(scale, s, ease),
        hero: () => rig.hero(),
        heroCtl: () => rig.heroCtl,
        cameraYaw: () => rig.control().cameraYaw,
        respawnAt: () => MARKET_SPAWN,
        terrainAt: (x, z) => rig.terrainAt(x, z),
        otherPose: (id) => {
          if (!things || id < BREAKABLE_IDS) return null;
          const p = things.content.placements.filter((x) => x.kind === "breakable")[id - BREAKABLE_IDS];
          return p ? { feet: things.at(p.id), yaw: p.yaw } : null;
        },
        moveFor: (e, m) => r.moveFor(e, m),
        ownedWeapons: () => r.ownedWeapons(),
        encounter: null,
        director: false,
        difficulty: "normal",
      });
      const t = createThings({ collision: rig.collision, actions, rng, content: thingsContent, fight: () => f, nav, hero: () => rig.hero(), groundAt: marketHeight, bag: { inventory: r.inventory, state: () => r.state().bag }, others: () => [r.others] });
      fight = f;
      things = t;
      rpg = r;
      return {
        modules: [...f.modules, ...t.modules, ...r.modules],
        build: (w) => {
          f.build(w);
          t.build(w);
          r.build(w);
        },
        hashed: [...f.hashed, ...t.hashed, ...r.hashed],
        heroDrive: () => t.heroDrive(),
        claimsInteract: () => t.claimsInteract(),
        heroScale: () => r.heroScale(),
      };
    },
  };
  const need = <T>(v: T | null, what: string): T => {
    if (!v) throw new Error(`market lab: ${what} not set up yet`);
    return v;
  };
  return {
    variant,
    content,
    conditions,
    standing,
    rpg: () => need(rpg, "the rpg layer"),
    things: () => need(things, "the things"),
    fight: () => need(fight, "the fight"),
  };
}
